import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { Router } from '@angular/router';
import { BdeoToastService } from '@bdeo/webcomponents';
import { Company, IField } from '@bdeo-motor/smartphoto-entities';
import { CompanyService } from '../../services/company.service';
import { CaseService } from '../../services/case.service';
import { SmartphotoService } from '../../services/smartphoto.service';
import { CreateField } from '../../entities/types/smartphoto-create-field.type';
import { Errors } from '../../entities/enums/errors.enum';

type ValidationErrors = {
  case?: string;
};

type DisableableField = IField & {
  disabled?: boolean;
};
@Component({
  selector: 'app-smartphoto-create',
  templateUrl: './smartphoto-create.component.html',
  styleUrl: './smartphoto-create.component.scss'
})
export class SmartphotoCreateComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly companyService = inject(CompanyService);
  private readonly caseService = inject(CaseService);
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18NextService = inject(I18NEXT_SERVICE);

  public company$ = this.companyService.getCurrentCompanyObservable();
  public createFields: Company['createFields'] = [];
  public fields: DisableableField[] = [];
  public loading = false;
  public createForm!: FormGroup;
  public deadLineMinDate: number | undefined;
  public oneSmartphotoPerCase?: boolean;
  public errors: ValidationErrors = {};

  public async ngOnInit(): Promise<void> {
    this.company$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (company) => {
      this.oneSmartphotoPerCase = company?.smartphoto?.onePerCase;
      this.createFields = company?.createFields ?? [];

      const fieldPromises = this.createFields.map(async (field) => {
        const fieldToAdd: CreateField = {
          label: field.fieldDescription ? field.fieldDescription : '',
          properties: [field.fieldName],
          type: field.dataType,
          required: field.mandatory,
          placeholder: field.fieldDescription ? field.fieldDescription : ''
        };

        if (field.dataType === 'list' && field.listName) {
          fieldToAdd['options'] = await this.companyService.getSelectorValues(field.listName);
        }

        return fieldToAdd;
      });

      this.fields = await Promise.all(fieldPromises);

      if (company?.smartphoto.claimTypes) {
        this.fields.push({
          label: this.i18NextService.t('brokers:claim_type'),
          properties: ['claimType'],
          type: 'list',
          value: company?.smartphoto.claimTypes.find((type) => type.default)?.value,
          options: company.smartphoto.claimTypes.map((type, index) => ({
            text: type.name,
            value: type.value,
            disabled: false,
            selected: type.default ?? false,
            order: index,
            name: type.name
          })),
          required: true
        });
      }

      if (company?.costEstimationInfo?.paintSystemConfig) {
        this.fields.push({
          label: this.i18NextService.t('brokers:paint_system'),
          properties: ['paintSystem'],
          type: 'list',
          value:
            'twoCoat' in company.costEstimationInfo.paintSystemConfig
              ? 'twoCoat'
              : Object.keys(company.costEstimationInfo.paintSystemConfig)[0],
          required: true,
          options: Object.keys(company.costEstimationInfo.paintSystemConfig).map((key, index) => ({
            text: this.i18NextService.t(`brokers:paint_systems.${key}`),
            value: key,
            disabled: false,
            selected: index === 0,
            order: index,
            name: this.i18NextService.t(`brokers:paint_systems.${key}`)
          }))
        });
      } else {
        this.fields.push({
          label: this.i18NextService.t('brokers:paint_system'),
          properties: ['paintSystem'],
          type: 'list',
          value: 'twoCoat',
          required: true,
          disabled: true,
          options: [
            {
              text: this.i18NextService.t('brokers:paint_systems.twoCoat'),
              name: this.i18NextService.t('brokers:paint_systems.twoCoat'),
              value: 'twoCoat',
              disabled: false,
              selected: true,
              order: 0
            }
          ]
        });
      }

      if (company?.deadlineSelfadjust) {
        this.deadLineMinDate = Date.now();
        this.fields.push({
          label: this.i18NextService.t('brokers:deadline'),
          placeholder: this.i18NextService.t('brokers:deadline'),
          properties: ['deadlineSelfadjust'],
          type: 'date',
          required: true
        });
      }

      const selfadjustInfo = company?.selfadjustInfo;
      const templates = Object.keys(selfadjustInfo?.pages || {});
      const isDefaultTemplateInTemplates = templates.some((key) => key === selfadjustInfo?.defaultTemplate);
      if (selfadjustInfo && !Array.isArray(selfadjustInfo.pages)) {
        this.fields.push({
          label: this.i18NextService.t('brokers:template'),
          properties: ['template'],
          type: 'list',
          value: selfadjustInfo?.defaultTemplate && isDefaultTemplateInTemplates ? selfadjustInfo.defaultTemplate : '',
          options: templates.map((template, index) => ({
            text: template,
            value: template,
            disabled: false,
            selected: false,
            order: index,
            name: template
          })),
          required: true
        });
      }

      this.buildFormControls();
    });
  }

  private buildFormControls(): void {
    const formFields: Record<string, FormControl> = {
      case: new FormControl('', Validators.required)
    };

    this.fields?.forEach((field) => {
      const validators: ValidatorFn[] = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      formFields[field.properties[0]] = new FormControl(field.value ?? '', validators);

      if (field.disabled) {
        formFields[field.properties[0]].disable({ onlySelf: true });
      }
    });

    this.createForm = this.formBuilder.group(formFields);
  }

  private parseErrors(error: HttpErrorResponse): void {
    if (error.error.errorMessage === Errors.BACKEND_ERROR_CASE_HAS_SELFADJUST) {
      this.errors.case = this.i18NextService.t('brokers:errors.case_already_has_smartphoto');
    }
  }

  public async createSmartphoto(): Promise<void> {
    this.errors = {};
    let caseID = '';
    this.loading = true;

    try {
      const caseResponse = await this.caseService.getByNumber(this.createForm.get('case')?.value, {
        normalized: this.oneSmartphotoPerCase
      });
      caseID = caseResponse.id;
    } catch (error) {
      try {
        const caseResponse = await this.caseService.create(this.createForm.get('case')?.value);
        caseID = caseResponse.id;
      } catch (err) {
        this.loading = false;
        return;
      }
    }

    try {
      const createRequestPayload = this.createForm.value;
      if (createRequestPayload.deadlineSelfadjust) {
        createRequestPayload.deadlineSelfadjust = new Date(createRequestPayload.deadlineSelfadjust).toLocaleDateString('es-ES');
      }

      createRequestPayload.costEstimationInfo = { paintSystem: createRequestPayload.paintSystem ?? 'twoCoat' };
      delete createRequestPayload.paintSystem;

      const createSmartphotoResponse = await this.smartphotoService.create(caseID, createRequestPayload);
      if (createSmartphotoResponse.success) {
        const createdSmartphotoID = createSmartphotoResponse.id;
        await this.router.navigate([`/smartphoto/${createdSmartphotoID}`]);
      } else {
        BdeoToastService.error(this.i18NextService.t('brokers:errors.case_creation_error'));
      }
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        this.parseErrors(e);
      }
      console.log('Error creating smartphoto: ', e);
    }

    this.loading = false;
  }
}
